import { formatCurrency } from '@/utils/numberFormat';
import { formatDateShort } from '@/utils/datePresentation';
import { FEES_TEXT } from '@/texts/catalog/fees';

const METRICS_TEXT = FEES_TEXT.metrics;

const buildLatestExpenseProviderName = (latestExpense = {}) => {
  const providerName = String(
    latestExpense?.provider_name
    || latestExpense?.provider?.name
    || latestExpense?.supplier?.name
    || latestExpense?.provider
    || ''
  ).trim();

  if (providerName !== '') {
    return providerName;
  }

  const commissionAgentName = String(latestExpense?.commission_agent_name || '').trim();

  if (commissionAgentName !== '') {
    return commissionAgentName;
  }

  return '-';
};

const buildLatestExpenseProviderId = (latestExpense = {}) => {
  return Number(
    latestExpense?.provider_id
    || latestExpense?.provider?.id
    || latestExpense?.supplier?.id
    || 0
  ) || 0;
};

const buildLatestExpenseConceptLabel = (latestExpense = {}) => {
  const concept = String(latestExpense?.concept || '').trim();

  return concept || '-';
};

const buildLatestExpenseMeta = (latestExpense = {}) => {
  const amount = formatCurrency(latestExpense?.amount || 0);
  const dateLabel = formatDateShort(latestExpense?.created_at || latestExpense?.date);

  return `${amount} | ${METRICS_TEXT.latestMetaPrefix} ${dateLabel}`;
};

export const createInitialFeesMetricsValues = () => ({
  total_expenses: 0,
  pending_to_pay: 0,
  paid: 0,
  latest_expense: {
    id: 0,
    provider_id: 0,
    provider_name: '',
    reference: '',
    concept: '',
    commission_agent_name: '',
    commission_agent_type: '',
    amount: 0,
    created_at: null,
    date: null,
  },
});

export const mapFeesMetricsPayload = (payload) => {
  const latestExpense = payload?.latest_expense && typeof payload.latest_expense === 'object'
    ? payload.latest_expense
    : {};

  return {
    total_expenses: Number(payload?.total_expenses ?? payload?.total ?? 0) || 0,
    pending_to_pay: Number(payload?.pending_to_pay ?? payload?.pending ?? 0) || 0,
    paid: Number(payload?.paid ?? payload?.total_paid ?? 0) || 0,
    latest_expense: {
      id: Number(latestExpense?.id ?? 0) || 0,
      provider_id: Number(latestExpense?.provider_id ?? latestExpense?.provider?.id ?? latestExpense?.supplier?.id ?? 0) || 0,
      provider_name: String(
        latestExpense?.provider_name
        ?? latestExpense?.provider?.name
        ?? latestExpense?.supplier?.name
        ?? latestExpense?.provider
        ?? ''
      ).trim(),
      reference: String(latestExpense?.reference ?? payload?.last_expense ?? '').trim(),
      concept: String(latestExpense?.concept ?? '').trim(),
      commission_agent_name: String(latestExpense?.commission_agent_name ?? latestExpense?.commission_agent?.name ?? '').trim(),
      commission_agent_type: String(latestExpense?.commission_agent_type ?? latestExpense?.commission_agent?.type ?? '').trim(),
      amount: Number(latestExpense?.amount ?? 0) || 0,
      created_at: latestExpense?.created_at || null,
      date: latestExpense?.date || null,
    },
  };
};

export const buildFeesMetricsFromRows = (rows) => {
  const safeRows = Array.isArray(rows) ? rows : [];

  const totals = safeRows.reduce((acc, row) => {
    const amount = Number(row?.amount || 0) || 0;
    const status = String(row?.status || '').trim().toLowerCase();

    acc.total_expenses += amount;

    if (status === 'pagado') {
      acc.paid += amount;
    } else {
      acc.pending_to_pay += amount;
    }

    return acc;
  }, {
    total_expenses: 0,
    pending_to_pay: 0,
    paid: 0,
  });

  const latestExpense = [...safeRows].sort((left, right) => {
    const leftTime = new Date(left?.created_at || left?.date || 0).getTime();
    const rightTime = new Date(right?.created_at || right?.date || 0).getTime();

    return rightTime - leftTime;
  })[0] || null;

  return {
    total_expenses: totals.total_expenses,
    pending_to_pay: totals.pending_to_pay,
    paid: totals.paid,
    latest_expense: {
      id: Number(latestExpense?.id || 0),
      provider_id: Number(
        latestExpense?.provider_id
        || latestExpense?.provider?.id
        || latestExpense?.supplier?.id
        || 0
      ) || 0,
      provider_name: String(
        latestExpense?.provider_name
        || latestExpense?.provider?.name
        || latestExpense?.supplier?.name
        || latestExpense?.provider
        || ''
      ).trim(),
      reference: String(latestExpense?.reference || '').trim(),
      concept: String(latestExpense?.concept || '').trim(),
      commission_agent_name: String(latestExpense?.commission_agent_name || latestExpense?.provider || latestExpense?.collaborator || '').trim(),
      commission_agent_type: String(latestExpense?.commission_agent_type || (latestExpense?.collaborator_id ? 'collaborator' : 'supplier') || '').trim(),
      amount: Number(latestExpense?.amount || 0) || 0,
      created_at: latestExpense?.created_at || null,
      date: latestExpense?.date || null,
    },
  };
};

export const buildFeesKpiCards = (metricsValues) => {
  const latestExpense = metricsValues?.latest_expense || {};
  const latestProviderId = buildLatestExpenseProviderId(latestExpense);
  const latestProviderRoute = latestProviderId > 0
    ? { name: 'supplierDetail', params: { supplierId: latestProviderId } }
    : null;

  return [
    {
      title: METRICS_TEXT.kpis.total,
      value: formatCurrency(metricsValues?.total_expenses),
      icon: 'fa-credit-card',
      iconBg: 'rgba(0, 112, 205, 0.12)',
      iconColor: '#0070cd',
    },
    {
      title: METRICS_TEXT.kpis.pendingToPay,
      value: formatCurrency(metricsValues?.pending_to_pay),
      icon: 'fa-clock-o',
      iconBg: 'rgba(239, 68, 68, 0.12)',
      iconColor: '#ef4444',
    },
    {
      title: METRICS_TEXT.kpis.paid,
      value: formatCurrency(metricsValues?.paid),
      icon: 'fa-check-circle',
      iconBg: 'rgba(59, 212, 46, 0.12)',
      iconColor: '#3bd42e',
    },
    {
      title: METRICS_TEXT.kpis.latest,
      value: buildLatestExpenseProviderName(latestExpense),
      valueRoute: latestProviderRoute,
      valueLinkClass: latestProviderRoute ? 'kpi-value-link-customer' : '',
      valueMetaLines: [
        buildLatestExpenseConceptLabel(latestExpense),
        buildLatestExpenseMeta(latestExpense),
      ],
      truncateValue: true,
      valueScale: 0.7,
      icon: 'fa-file-text-o',
      iconBg: 'rgba(77, 191, 180, 0.12)',
      iconColor: '#4dbfb4',
    },
  ];
};
